<script lang="ts" setup>
import {ease} from '@/libs/animation/easer'
import {computed, PropType, ref, watch} from 'vue'
import {useInViewport} from '@/helper/useInViewport'
import {Money} from '@/graphql/generated/graphql'
import money from "@/helper/money";

const emit = defineEmits(['done'])

const { target, isInViewport } = useInViewport({
  threshold: 0.1,
  once: true
})

const progress = ref(0)
const value = computed(() => {
    if (money.isMoney(props.to) && money.isMoney(props.from)) {
        return props.formatter({
            amount: props.from.amount + ((props.to.amount - props.from.amount) * progress.value),
            currency: props.to.currency
        })
    } else if (typeof props.to === 'number' && typeof props.from === 'number') {
        let current = props.from + ((props.to - props.from) * progress.value)
        if (Number.isInteger(props.to)) {
            current = Math.round(current)
        }
        return props.formatter(current)
    }
})

watch(isInViewport, (v) => {
  if (v) {
    if (props.duration) {
        ease({
            duration: props.duration,
            delay: props.delay,
            onTick: (prog: number) => progress.value = prog,
            easing: props.easing,
        })
    }
    else {
        progress.value = 1
    }
  }
}, { immediate: true })

watch(progress, v => {
    if (v === 1) {
        emit('done')
    }
})

const props = defineProps({
    to: {
        type: [Object, Number] as PropType<Money|number>,
        required: false,
        default: 0
    },
    from: {
        type: [Object, Number] as PropType<Money|number>,
        required: false,
        default: (rawProps: { to: Money|number }) =>  money.isMoney(rawProps.to) ? money.Zero : 0
    },
    formatter: {
        type: Function,
        required: false,
        default: (value: number|Money) => value
    },
    duration: {
        type: Number,
        required: false,
        default: 1000
    },
    delay: {
        type: Number,
        required: false,
        default: 0
    },
    manual: {
        type: Boolean,
        required: false,
        default: false
    },
    easing: {
        type: String,
        required: false,
        default: 'easingFnOutSin'
    }
})
</script>

<template>
    <div ref="target" class="counting-number">{{ value }}</div>
</template>

<style lang="scss" scoped>
.counting-number {
    font-size: inherit;
    font-weight: inherit;
    font-style: inherit;
    color: inherit;
}
</style>
